/* =====================================================================
 * ConfirmDialog — 공통 확인 다이얼로그 (테마 기반)
 *
 * `window.confirm` 을 대체하는 앱 테마 일관 UI. 공용 `Modal` 을 감싸서 제목,
 * 본문 설명, 취소/확인 버튼 두 개만 노출한다. 삭제 같은 파괴적 동작은
 * `tone="danger"` 로 확인 버튼의 **글자색만** 빨간 색으로 표시한다 (배경은
 * 그대로 subtle surface 유지) — 앱 스타일 가이드에 맞춰 너무 튀지 않도록.
 * ===================================================================== */

import React, { useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Modal } from './Modal';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** 본문 설명. ReactNode 로 받아 줄바꿈/강조 표시가 가능하다. */
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
  /** 확인 버튼 클릭 시 호출. 비동기면 pending 표시 후 close 전 완료까지 대기. */
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
  /** 확인 버튼이 처리 중일 때 외부에서 강제 로딩 주입할 수 있는 optional prop. */
  loading?: boolean;
  width?: string;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = 'default',
  onConfirm,
  onClose,
  loading,
  width = '420px',
}) => {
  const { t } = useTranslation('common');
  const [internalLoading, setInternalLoading] = React.useState(false);
  const isLoading = loading ?? internalLoading;

  useEffect(() => {
    if (!open) {
      setInternalLoading(false);
    }
  }, [open]);

  const handleConfirm = useCallback(async () => {
    if (isLoading) return;
    try {
      setInternalLoading(true);
      await onConfirm();
    } finally {
      setInternalLoading(false);
    }
  }, [isLoading, onConfirm]);

  const resolvedCancel = cancelLabel ?? t('common.cancel', { defaultValue: '취소' });
  const resolvedConfirm =
    confirmLabel ??
    (tone === 'danger'
      ? t('common.delete', { defaultValue: '삭제' })
      : t('common.confirm', { defaultValue: '확인' }));

  return (
    <Modal
      open={open}
      onClose={() => {
        if (isLoading) return;
        onClose();
      }}
      title={title}
      width={width}
      bodyPadding="0 24px 20px"
      closeOnBackdrop={!isLoading}
    >
      {description && <Description>{description}</Description>}
      <Footer>
        <CancelButton
          type="button"
          onClick={onClose}
          disabled={isLoading}
        >
          {resolvedCancel}
        </CancelButton>
        <ConfirmButton
          type="button"
          $tone={tone}
          onClick={handleConfirm}
          disabled={isLoading}
          autoFocus
        >
          {isLoading
            ? t('common.processing', { defaultValue: '처리 중…' })
            : resolvedConfirm}
        </ConfirmButton>
      </Footer>
    </Modal>
  );
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Description = styled.div`
  font-size: 0.9375rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: pre-line;
  margin-bottom: 20px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const BaseButton = styled.button`
  min-height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast};

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(BaseButton)`
  color: ${({ theme }) => theme.colors.textSecondary};
  background: transparent;
  border-color: transparent;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.textPrimary};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'};
  }
`;

// 확인 버튼은 앱 톤에 맞춰 배경은 subtle surface 로 두고, `danger` 인 경우에만
// **글자색**을 error 색으로 바꿔 파괴적 동작을 경고한다. 배경 자체를 빨간색
// 으로 칠하면 강도가 과해 UI 톤이 깨지므로 의도적으로 피한다.
const ConfirmButton = styled(BaseButton)<{ $tone: 'default' | 'danger' }>`
  ${({ theme, $tone }) =>
    $tone === 'danger'
      ? css`
          color: ${theme.colors.error};
          background: ${theme.mode === 'dark'
            ? 'rgba(248, 113, 113, 0.08)'
            : 'rgba(239, 68, 68, 0.06)'};
          border-color: ${theme.mode === 'dark'
            ? 'rgba(248, 113, 113, 0.28)'
            : 'rgba(239, 68, 68, 0.22)'};

          &:hover:not(:disabled) {
            background: ${theme.mode === 'dark'
              ? 'rgba(248, 113, 113, 0.15)'
              : 'rgba(239, 68, 68, 0.1)'};
            border-color: ${theme.colors.error};
          }
        `
      : css`
          color: ${theme.colors.textPrimary};
          background: ${theme.mode === 'dark'
            ? 'rgba(255,255,255,0.1)'
            : 'rgba(0,0,0,0.06)'};
          border-color: ${theme.mode === 'dark'
            ? 'rgba(255,255,255,0.16)'
            : 'rgba(0,0,0,0.1)'};

          &:hover:not(:disabled) {
            background: ${theme.mode === 'dark'
              ? 'rgba(255,255,255,0.14)'
              : 'rgba(0,0,0,0.09)'};
            border-color: ${theme.colors.borderInput};
          }
        `}
`;
